import React, { Component } from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	IconButton,
	Box,
	ListItemText,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import { marked } from 'marked';
import { withTheme } from '@mui/styles';
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import {UrlBuilder} from "../../core/url/UrlBuilder";

const styles = theme => ({
	button: { textTransform: 'none' },
	dialogPaper: {
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 400,
		maxWidth: 600,
		height: '70vh',
		padding: 0,
		overflow: 'hidden',
		[theme.breakpoints.down('sm')]: {
			height: '90vh',
			minWidth: 'unset',
			maxWidth: '100%',
		},
	},
	messageList: {
		flex: 1,
		overflowY: 'auto',
		padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)} ${theme.spacing(1.5)}`,
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1.5),
		wordBreak: 'break-word',
		boxSizing: 'border-box',
	},
	message: {
		padding: theme.spacing(1.25),
		borderRadius: theme.shape.borderRadius * 1.25,
		maxWidth: '95%',
		wordWrap: 'break-word',
	},
	dialogTitle: {
		paddingBottom: 0,
	},
	userMessage: {
		alignSelf: 'flex-end',
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
	},
	aiMessage: {
		alignSelf: 'flex-start',
		backgroundColor: 'transparent',
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5),
	},
	feedbackButtons: {
		display: 'flex',
		gap: theme.spacing(1),
		marginTop: theme.spacing(0.5),
	},
	inputContainer: {
		display: 'flex',
		alignItems: 'center',
		padding: theme.spacing(1.5),
		borderTop: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		boxSizing: 'border-box',
		flexShrink: 0,
		width: '100%',
	},
	textField: {
		flex: 1,
		marginRight: theme.spacing(1),
	},
	thinkingText: {
		display: 'inline-flex',
		alignItems: 'center',
	},
});

const SUGGESTIONS_TO_SHOW = 4;

const pickRandomSuggestions = (pool, count) => {
	if (!pool || pool.length === 0) return [];
	if (pool.length <= count) return [...pool];
	const copy = [...pool];
	for (let i = copy.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[copy[i], copy[j]] = [copy[j], copy[i]];
	}
	return copy.slice(0, count);
};

class AiChatButton extends Component {
	constructor(props) {
		super(props);
		this.state = {
			open: false,
			messages: [],
			input: '',
			loading: false,
			chatId: null,
			loadingText: 'Thinking',
			isInitialLoad: true,
			dots: '',
			displayedSuggestions: pickRandomSuggestions(props.presetSuggestions, SUGGESTIONS_TO_SHOW),
			showSuggestions: true,
			hasAsked: false,
			creditsRemaining: null, // null = unlimited or not yet known
		};
		this.dotInterval = null;
		this.initialLoadTexts = ['Analyzing data', 'Thinking', 'Generating summary'];
		this.messageEndRef = React.createRef();
		// Buffer for streaming text chunks — flushed to state once per animation frame
		this.streamBuffer = '';
		this.rafId = null;
	}

	resampleSuggestions = () => {
		this.setState({
			displayedSuggestions: pickRandomSuggestions(this.props.presetSuggestions, SUGGESTIONS_TO_SHOW),
		});
	};

	componentWillUnmount() {
		this.stopDotAnimation();
		clearInterval(this.loadingTextInterval);
		if (this.rafId) cancelAnimationFrame(this.rafId);
	}

	// Accumulate a streaming chunk and schedule a batched state update
	bufferStreamChunk = (text) => {
		this.streamBuffer += text;
		if (!this.rafId) {
			this.rafId = requestAnimationFrame(this.flushStreamBuffer);
		}
	};

	flushStreamBuffer = () => {
		this.rafId = null;
		if (!this.streamBuffer) return;
		const toAppend = this.streamBuffer;
		this.streamBuffer = '';
		this.setState(prevState => {
			const msgs = [...prevState.messages];
			const last = msgs[msgs.length - 1];
			if (last && last.sender === 'ai') {
				msgs[msgs.length - 1] = { ...last, text: last.text + toAppend };
			}
			return { messages: msgs };
		}, this.scrollToBottom);
	};

	consumeSseStream = async (response) => {
		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';
		let hasStartedStreaming = false;

		while (true) {
			const { done, value } = await reader.read();
			if (done) break;

			buffer += decoder.decode(value, { stream: true });

			const parts = buffer.split('\n\n');
			buffer = parts.pop();

			for (const part of parts) {
				const dataLine = part.split('\n').find(l => l.startsWith('data: '));
				if (!dataLine) continue;

				let parsed;
				try { parsed = JSON.parse(dataLine.substring(6)); } catch { continue; }

				if (parsed.type === 'chunk') {
					if (!hasStartedStreaming) {
						hasStartedStreaming = true;
						this.stopDotAnimation();
						clearInterval(this.loadingTextInterval);
						this.setState(prevState => ({
							loading: false,
							messages: [...prevState.messages, {
								sender: 'ai',
								text: parsed.text,
								feedback: null,
							}],
						}), this.scrollToBottom);
					} else {
						this.bufferStreamChunk(parsed.text);
					}
				} else if (parsed.type === 'done') {
					if (this.rafId) {
						cancelAnimationFrame(this.rafId);
						this.rafId = null;
					}
					this.flushStreamBuffer();
					this.setState(prevState => {
						const msgs = [...prevState.messages];
						const last = msgs[msgs.length - 1];
						if (last && last.sender === 'ai') {
							msgs[msgs.length - 1] = { ...last, chatMessageId: parsed.chatMessageId };
						}
						return {
							messages: msgs,
							chatId: parsed.chatId ?? prevState.chatId,
							showSuggestions: true,
							displayedSuggestions: pickRandomSuggestions(this.props.presetSuggestions, SUGGESTIONS_TO_SHOW),
							creditsRemaining: parsed.creditsRemaining ?? prevState.creditsRemaining,
						};
					});
				} else if (parsed.type === 'error') {
					this.stopDotAnimation();
					clearInterval(this.loadingTextInterval);
					this.setState(prevState => ({
						loading: false,
						messages: [...prevState.messages, {
							sender: 'ai',
							text: '⚠️ The AI service encountered an error. Please try again.',
						}],
					}));
				}
			}
		}

		if (!hasStartedStreaming) {
			this.stopDotAnimation();
			clearInterval(this.loadingTextInterval);
			this.setState({ loading: false });
		}
	};

	startDotAnimation = () => {
		let count = 0;
		this.dotInterval = setInterval(() => {
			count = (count + 1) % 4;
			this.setState({ dots: '.'.repeat(count) });
		}, 400);
	};

	stopDotAnimation = () => {
		if (this.dotInterval) clearInterval(this.dotInterval);
		this.setState({ dots: '' });
	};

	scrollToBottom = () => {
		if (this.messageEndRef.current) {
			this.messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	};

	handleOpen = () => {
		const { initialChatId, initialMessage } = this.props;
		const hasInitialContext = initialChatId && initialMessage;

		this.setState({
			open: true,
			loading: false,
			isInitialLoad: !hasInitialContext,
			messages: hasInitialContext
				? [{ sender: 'ai', text: initialMessage, feedback: null }]
				: [],
			showSuggestions: true,
			chatId: hasInitialContext ? initialChatId : null,
			input: '',
			displayedSuggestions: pickRandomSuggestions(this.props.presetSuggestions, SUGGESTIONS_TO_SHOW),
		});
	};

	runDefaultAction = async () => {
		this.setState({
			loading: true,
			isInitialLoad: true,
			loadingText: this.initialLoadTexts[0],
			showSuggestions: false,
		});
		this.startDotAnimation();

		let i = 1;
		this.loadingTextInterval = setInterval(() => {
			if (i < this.initialLoadTexts.length) {
				this.setState({ loadingText: this.initialLoadTexts[i] });
				i++;
			} else {
				clearInterval(this.loadingTextInterval);
			}
		}, 1500);

		try {
			const response = await fetch(this.props.apiUrl);

			if (response.status === 402) {
				this.stopDotAnimation();
				clearInterval(this.loadingTextInterval);
				const data = await response.json();
				const resetNote = data.resetAt
					? `*Your credits reset on ${new Date(data.resetAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}.*`
					: '';
				this.setState({
					loading: false,
					creditsRemaining: 0,
					showSuggestions: false,
					messages: [{ sender: 'ai', text: `**You've used all your free AI credits for this month.**\n\nFree accounts include 15 AI requests per month. Upgrade in Settings to get 150 requests monthly — plus deeper workout analysis, exercise breakdowns, and ongoing coaching conversations.\n\n${resetNote}` }],
				});
				return false;
			}

			if (!response.ok) {
				throw new Error(`Server returned ${response.status}`);
			}

			await this.consumeSseStream(response);
			return true;
		} catch (error) {
			console.error('Error fetching AI summary:', error);
			this.stopDotAnimation();
			clearInterval(this.loadingTextInterval);

			this.setState({
				loading: false,
				isInitialLoad: false,
				messages: [
					{
						sender: 'ai',
						text: '⚠️ The AI service is not available at the moment. Please try again later.',
					},
				],
			});
			return false;
		}
	};

	handleClose = () => {
		this.setState({
			open: false,
			messages: [],
			input: '',
			chatId: null,
			isInitialLoad: true,
			hasAsked: false,
			creditsRemaining: null,
		});
		this.stopDotAnimation();
		clearInterval(this.loadingTextInterval);
		if (this.props.onClose) this.props.onClose();
	};

	componentDidUpdate(prevProps) {
		if (this.props.externalOpen && !prevProps.externalOpen) {
			this.handleOpen();
		}
		if (!this.props.externalOpen && prevProps.externalOpen && this.state.open) {
			this.handleClose();
		}
	}

	handleInputChange = event => {
		this.setState({ input: event.target.value });
	};

	handleSend = async () => {
		const { input } = this.state;
		if (!input.trim() || this.state.loading) return;

		let { chatId } = this.state;

		// If no chat exists yet, create a lightweight one (system prompt only,
		// no summarize call, no credit consumed). The AI will use function
		// calling to fetch whatever data it needs to answer the question.
		if (!chatId) {
			try {
				const res = await fetch(UrlBuilder.ai.createChat(), {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ pageContext: this.props.pageContext || null }),
				});
				if (!res.ok) return;
				const data = await res.json();
				chatId = data.chatId;
				this.setState({ chatId });
			} catch {
				return;
			}
		}

		const { input: currentInput, messages } = this.state;
		if (!currentInput.trim()) return;

		this.setState(
			{
				messages: [...messages, { sender: 'user', text: currentInput }],
				input: '',
				loading: true,
				loadingText: 'Thinking',
				isInitialLoad: false,
				showSuggestions: false,
				hasAsked: true,
			},
			this.scrollToBottom
		);

		this.startDotAnimation();

		try {
			const response = await fetch(UrlBuilder.ai.userAskStream(), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ chatId, question: currentInput }),
			});

			if (response.status === 402) {
				this.stopDotAnimation();
				const data = await response.json();
				const resetNote = data.resetAt
					? `*Your credits reset on ${new Date(data.resetAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}.*`
					: '';
				this.setState(prevState => ({
					loading: false,
					creditsRemaining: 0,
					messages: [...prevState.messages, {
						sender: 'ai',
						text: `**That was your last AI credit for this month.**\n\nUpgrade in Settings to keep the conversation going — 150 requests per month, full workout analysis, and everything you need to train smarter.\n\n${resetNote}`,
					}],
				}));
				return;
			}

			if (!response.ok) {
				throw new Error(`Server returned ${response.status}`);
			}

			await this.consumeSseStream(response);
		} catch (error) {
			console.error('Error sending message:', error);
			this.stopDotAnimation();
			this.setState({ loading: false });
		}
	};

	handleFeedback = async (index, value) => {
		if (this.state.messages[index].feedback === value) {
			return;
		}

		this.setState(prevState => {
			const updated = [...prevState.messages];
			updated[index].feedback = value;
			return { messages: updated };
		});

		const message = this.state.messages[index];
		const chatMessageId = message.chatMessageId;

		await fetch(UrlBuilder.ai.chatFeedback(), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				chatMessageId,
				feedback: value, // 'up' or 'down'
			}),
		});
	};


	handleKeyPress = event => {
		const isEnter = event.key === 'Enter';
		const isShift = event.shiftKey;
		const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

		if (isMobile) return;

		if (isEnter && !isShift && !this.state.loading) {
			event.preventDefault();
			this.handleSend();
		}
	};

	renderAiMessage(text, idx, feedback) {
		const { classes } = this.props;

		// Escape all single tildes
		const escapedText = text.replace(/~/g, "\\~");

		return (
			<Box
				key={idx}
				className={`${classes.message} ${classes.aiMessage}`}
				sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}
			>
				<Box
					dangerouslySetInnerHTML={{ __html: marked(escapedText) }}
					sx={{ fontSize: '14px', lineHeight: '1.5', flex: 1 }}
				/>
				<Box
					className={classes.feedbackButtons}
					sx={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						marginTop: '2px',
					}}
				>
					<IconButton
						size="small"
						color={feedback === 'up' ? 'success' : 'default'}
						onClick={() => this.handleFeedback(idx, 'up')}
					>
						<ThumbUpAltOutlinedIcon fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						color={feedback === 'down' ? 'error' : 'default'}
						onClick={() => this.handleFeedback(idx, 'down')}
					>
						<ThumbDownAltOutlinedIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>
		);
	}

	renderUserMessage(text, idx) {
		const { classes } = this.props;
		return (
			<Box key={idx} className={`${classes.message} ${classes.userMessage}`}>
				<ListItemText
					primary={text}
					primaryTypographyProps={{
						style: { fontSize: 14, lineHeight: 1.5 },
					}}
				/>
			</Box>
		);
	}

	handleSuggestionClick = (text) => {
		this.setState({ input: text }, () => {
			this.handleSend();
		});
	};

	renderLoadingMessage() {
		const { classes } = this.props;
		const { loadingText, dots } = this.state;
		return (
			<Box
				className={`${classes.message} ${classes.aiMessage}`}
			>
				<ListItemText
					primary={
						<span className={classes.thinkingText}>
							{loadingText}<span>{dots}</span
						></span>
					}
					sx={{ paddingTop: 2 }}
				/>
			</Box>
		);
	}

	render() {
		const { classes } = this.props;
		const { open, messages, input, loading, creditsRemaining, chatId } = this.state;
		const outOfCredits = creditsRemaining !== null && creditsRemaining <= 0;
		const defaultActionLabel = this.props.defaultActionLabel || 'Summarize';
		const showEmptyState = !loading && messages.length === 0 && !chatId;

		return (
			<>
				{this.props.triggerComponent
					? React.cloneElement(this.props.triggerComponent, { onClick: this.handleOpen })
					: <Button
						onClick={() => this.handleOpen()}
						title="AI Summary"
						color='primary'
						variant="contained"
					>
						<AutoAwesomeIcon />
					</Button>
				}

				<Dialog
					open={open}
					onClose={this.handleClose}
					fullWidth
					maxWidth="sm"
					classes={{ paper: classes.dialogPaper }}
				>
					<DialogTitle className={classes.dialogTitle} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						AI Insights
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							{creditsRemaining !== null && (
								<Typography variant="caption" color="textSecondary">
									{creditsRemaining} credit{creditsRemaining !== 1 ? 's' : ''} left
								</Typography>
							)}
							<IconButton
								aria-label="close"
								onClick={this.handleClose}
								size="small"
							>
								<CloseIcon />
							</IconButton>
						</Box>
					</DialogTitle>
					<DialogContent className={classes.dialogContent}>
						<Box className={classes.messageList}>
							{showEmptyState && this.props.apiUrl && (
								<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 3, px: 2 }}>
									<Button
										variant="contained"
										color="primary"
										startIcon={<AutoAwesomeIcon />}
										onClick={this.runDefaultAction}
										disabled={outOfCredits}
										sx={{ textTransform: 'none', fontWeight: 500 }}
									>
										{defaultActionLabel}
									</Button>
									<Typography variant="caption" color="textSecondary" align="center">
										…or type your own question below.
									</Typography>
								</Box>
							)}

							{messages.map((msg, idx) =>
								msg.sender === 'ai'
									? this.renderAiMessage(msg.text, idx, msg.feedback)
									: this.renderUserMessage(msg.text, idx)
							)}
							{loading && this.renderLoadingMessage()}

							{this.state.showSuggestions && !this.state.hasAsked && this.state.displayedSuggestions && this.state.displayedSuggestions.length > 0 && (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
									<Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
										Try asking:
									</Typography>
									<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
										{this.state.displayedSuggestions.map((text, idx) => (
											<Box
												key={idx}
												onClick={() => this.handleSuggestionClick(text)}
												sx={(theme) => ({
													cursor: 'pointer',
													px: 1.5,
													py: 0.75,
													backgroundColor: theme.palette.action.hover,
													borderRadius: theme.shape.borderRadius,
													fontSize: 12,
													transition: 'background-color 0.2s',
													'&:hover': { backgroundColor: theme.palette.action.selected },
												})}
											>
												{text}
											</Box>
										))}
									</Box>
								</Box>
							)}

							<div ref={this.messageEndRef} />
						</Box>

						<Box className={classes.inputContainer}>
							<TextField
								variant="outlined"
								placeholder="Ask something..."
								value={input}
								onChange={this.handleInputChange}
								onKeyPress={this.handleKeyPress}
								className={classes.textField}
								disabled={loading || outOfCredits}
								multiline
								minRows={1}
								maxRows={4}
								InputProps={{ style: { overflow: 'auto' } }}
							/>
							<IconButton
								color="inherit"
								onClick={this.handleSend}
								disabled={loading || outOfCredits}
								sx={{ flexShrink: 0 }}
							>
								<SendIcon />
							</IconButton>
						</Box>
					</DialogContent>
				</Dialog>
			</>
		);
	}
}

export default withTheme(withStyles(styles, { withTheme: true })(AiChatButton));
